import type { BankTransferDetails } from '@/components/children/bankTransferDetails/scripts';
import type { Address, BankAccount, Logo, PaymentDetails, Person } from './types'
import { PaymentMethod } from '@/Enums';
import { isPlainObject } from './HelperFunctions';
import type { PropertyValidator } from './typeValidators';
import {
  hasValidProperties,
  isNumber,
  isString,
  isOptionalString,
  isOptionalNullableString,
  createEnumValidator,
} from './typeValidators';

// ==================== PERSON TYPE GUARD ====================

const personValidators: PropertyValidator[] = [
  { key: 'id', required: true, validator: isNumber },
  { key: 'firstName', required: false, validator: isOptionalString },
  { key: 'lastName', required: false, validator: isOptionalString },
  { key: 'phoneNumber', required: false, validator: isOptionalString },
  { key: 'email', required: false, validator: isOptionalString },
];

export function isPerson(value: unknown): value is Person {
  if (!isPlainObject(value)) return false;
  return hasValidProperties(value, personValidators);
}

// ==================== ADDRESS TYPE GUARD ====================

const addressValidators: PropertyValidator[] = [
  { key: 'id', required: true, validator: isNumber },
  { key: 'street', required: false, validator: isOptionalString },
  { key: 'town', required: false, validator: isOptionalString },
  { key: 'zipCode', required: false, validator: isOptionalString },
];

export function isAddress(value: unknown): value is Address {
  if (!isPlainObject(value)) return false;
  return hasValidProperties(value, addressValidators);
}

// ==================== BANK ACCOUNT TYPE GUARD ====================

const bankAccountValidators: PropertyValidator[] = [
  { key: 'id', required: true, validator: isNumber },
  { key: 'creditInstitute', required: true, validator: isString },
  { key: 'iban', required: true, validator: isString },
  { key: 'bic', required: true, validator: isString },
  { key: 'accountOwnerName', required: true, validator: isString },
];

export function isBankAccount(value: unknown): value is BankAccount {
  if (!isPlainObject(value)) return false;
  return hasValidProperties(value, bankAccountValidators);
}

export function isOptionalNullableBankAccount(value: unknown): boolean {
  return value === undefined || value === null || isBankAccount(value);
};

// ==================== BANK TRANSFER DETAILS TYPE GUARD ====================

const bankTransferDetailsValidators: PropertyValidator[] = [
  { key: 'id', required: true, validator: isNumber },
  { key: 'reference', required: false, validator: isOptionalNullableString },
];

export function isBankTransferDetails(value: unknown): value is BankTransferDetails {
  if (!isPlainObject(value)) return false;
  return hasValidProperties(value, bankTransferDetailsValidators);
}


// ==================== LOGO TYPE GUARD ====================

const logoValidators: PropertyValidator[] = [
  { key: 'id', required: true, validator: isNumber },
  { key: 'format', required: false, validator: isOptionalNullableString },
  { key: 'data', required: false, validator: isOptionalNullableString },
];

export function isLogo(value: unknown): value is Logo {
  if (!isPlainObject(value)) return false;
  return hasValidProperties(value, logoValidators);
}

export function isOptionalNullableLogo(value: unknown): boolean {
  return value === undefined || value === null || isLogo(value);
}

// ==================== PAYMENT DETAILS TYPE GUARD ====================

const isPaymentMethod = createEnumValidator(PaymentMethod);

const paymentDetailsValidators: PropertyValidator[] = [
  { key: 'paymentMethod', required: true, validator: isPaymentMethod },
  { key: 'sepaDebitDate', required: false, validator: isOptionalNullableString },
  { key: 'mandateReference', required: false, validator: isOptionalNullableString },
  { key: 'invoiceEmail', required: false, validator: isOptionalNullableString },
];

export function isPaymentDetails(value: unknown): value is PaymentDetails {
  if (!isPlainObject(value)) return false;
  return hasValidProperties(value, paymentDetailsValidators);
}


